// Analytics Dashboard - Comprehensive performance insights and learning analytics
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import {
  TrendingUp,
  Target,
  Clock,
  Award,
  BarChart3,
  Brain,
  AlertCircle,
  CheckCircle,
  Zap,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface AnalyticsData {
  performance_summary: any;
  top_mastered_techniques: any[];
  recently_practiced: any[];
  weak_areas: any[];
  performance_over_time: any[];
  performance_by_hour: any[];
  performance_by_day_of_week: any[];
  learning_insights: any[];
  mastery_distribution: any;
  recent_achievements: any[];
}

export default function Analytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-analytics`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      setAnalytics(data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          Loading analytics...
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          No analytics data available
        </div>
      </div>
    );
  }

  const summary = analytics.performance_summary;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              Analytics Dashboard
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Deep insights into your learning journey
          </p>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="strengths">Strengths & Weaknesses</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Total Sessions
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.total_sessions || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Overall Accuracy
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.overall_accuracy?.toFixed(1) || 0}%
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Problems Solved
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.total_problems_correct || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Avg Time/Problem
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.avg_time_per_problem?.toFixed(1) || 0}s
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Learning Insights */}
            {analytics.learning_insights.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    Learning Insights
                  </CardTitle>
                  <CardDescription>
                    Personalized recommendations for your learning journey
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.learning_insights.map((insight: any) => (
                      <div
                        key={insight.id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                              {insight.title}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {insight.description}
                            </p>
                            {insight.action_text && (
                              <button className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                                {insight.action_text} →
                              </button>
                            )}
                          </div>
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              insight.priority === "high"
                                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                : insight.priority === "medium"
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400"
                            }`}
                          >
                            {insight.priority}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Progress */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Recent Progress (Last 7 Days)
                </CardTitle>
                <CardDescription>Your performance trend</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Sessions
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.sessions_last_7_days || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Accuracy
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.accuracy_last_7_days?.toFixed(1) || 0}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Best Session
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.best_session_correct || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Practice Days
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.unique_practice_days || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Strengths & Weaknesses Tab */}
          <TabsContent value="strengths" className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Weak Areas */}
              {analytics.weak_areas.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      Areas for Improvement
                    </CardTitle>
                    <CardDescription>
                      Techniques that need more practice
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.weak_areas.map((area: any, idx: number) => (
                        <div
                          key={idx}
                          className="p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {area.technique_name}
                            </h4>
                            <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                              Priority: {area.priority_score?.toFixed(1)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">
                                Failure Rate:
                              </span>
                              <span className="ml-2 font-medium text-orange-600 dark:text-orange-400">
                                {area.failure_rate?.toFixed(1)}%
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">
                                Attempts:
                              </span>
                              <span className="ml-2 font-medium">
                                {area.recent_attempts}
                              </span>
                            </div>
                          </div>
                          <button className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                            Practice Now →
                          </button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Mastery Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-600" />
                    Mastery Distribution
                  </CardTitle>
                  <CardDescription>
                    Your skill level across all techniques
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(analytics.mastery_distribution).map(
                      ([level, count]: any) => (
                        <div key={level}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium capitalize text-gray-700 dark:text-gray-300">
                              {level}
                            </span>
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                              {count} techniques
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-full rounded-full ${
                                level === "mastered"
                                  ? "bg-green-600"
                                  : level === "proficient"
                                    ? "bg-blue-600"
                                    : level === "learning"
                                      ? "bg-yellow-600"
                                      : "bg-gray-400"
                              }`}
                              style={{
                                width: `${
                                  (count /
                                    (
                                      Object.values(
                                        analytics.mastery_distribution,
                                      ) as number[]
                                    ).reduce((a, b) => a + b, 0)) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Mastered Techniques */}
            {analytics.top_mastered_techniques.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-600" />
                    Top Mastered Techniques
                  </CardTitle>
                  <CardDescription>Your strongest techniques</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {analytics.top_mastered_techniques
                      .slice(0, 6)
                      .map((tech: any) => (
                        <div
                          key={tech.id}
                          className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {tech.techniques?.name || "Unknown Technique"}
                              </h4>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {tech.techniques?.category}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {tech.mastery_level}%
                              </div>
                              <div className="text-xs text-gray-500">
                                mastery
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                              <div className="text-gray-600 dark:text-gray-400 text-xs">
                                Accuracy
                              </div>
                              <div className="font-medium">
                                {tech.average_accuracy?.toFixed(1)}%
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600 dark:text-gray-400 text-xs">
                                Streak
                              </div>
                              <div className="font-medium">
                                {tech.current_streak}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600 dark:text-gray-400 text-xs">
                                Sessions
                              </div>
                              <div className="font-medium">
                                {tech.sessions_completed}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 flex gap-1">
                            {tech.easy_mastered && (
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs rounded">
                                Easy ✓
                              </span>
                            )}
                            {tech.medium_mastered && (
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 text-xs rounded">
                                Medium ✓
                              </span>
                            )}
                            {tech.hard_mastered && (
                              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 text-xs rounded">
                                Hard ✓
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
